<div class="pricing-detail core-screen">
  @if (loading()) {
    <p class="pricing-detail__loading" role="status">
      {{ 'admin.staffQrScan.loading' | translate }}
    </p>
  } @else if (version(); as detail) {
    <div class="pricing-detail__header">
      <div class="version-badge">v{{ detail.version_number }}</div>
      <div class="pricing-detail__meta">
        <p class="pricing-detail__dates">
          {{ 'admin.pricing.effectiveFrom' | translate }}:
          {{ formatDate(detail.effective_from) }}
          @if (detail.effective_to) {
            <span class="separator">·</span>
            {{ 'admin.pricing.effectiveTo' | translate }}:
            {{ formatDate(detail.effective_to) }}
          } @else {
            <span class="separator">·</span>
            {{ 'admin.pricing.effectiveOpen' | translate }}
          }
        </p>
        @if (domain() === 'storage' && storageConfig(); as storage) {
          <p class="pricing-detail__rate">
            {{ 'admin.pricing.baseRate' | translate }}:
            {{ formatRate(storage.baseRateByndPerM3Day) }}
            {{ 'admin.pricing.rateUnit' | translate }}
          </p>
        }
        @if (domain() === 'promocodes' && promocodesConfig(); as promos) {
          <p class="pricing-detail__rate">
            {{ 'admin.pricing.promocodeCount' | translate }}:
            {{ promos.promocodes.length }}
          </p>
        }
        @if (detail.comment) {
          <p class="pricing-detail__comment">{{ detail.comment }}</p>
        }
      </div>
      @if (isScheduled()) {
        <div class="pricing-detail__actions">
          <lib-not-button
            [text]="'admin.pricing.actions.edit' | translate"
            [color]="'#140342'"
            [bgColor]="'rgba(55, 190, 176, 0.12)'"
            [width]="'auto'"
            [size]="'medium'"
            [action]="{ type: 'action' }"
            (actionEvent)="onEdit()"
          />
          <lib-not-button
            [text]="'admin.pricing.actions.delete' | translate"
            [color]="'#B42318'"
            [bgColor]="'rgba(180, 35, 24, 0.08)'"
            [width]="'auto'"
            [size]="'medium'"
            [action]="{ type: 'action' }"
            [disabled]="deleting()"
            [isLoading]="deleting()"
            (actionEvent)="onDelete()"
          />
        </div>
      }
    </div>

    @if (domain() === 'storage' && storageConfig(); as storage) {
      <section class="pricing-detail__section">
        <h2>{{ 'admin.pricing.create.storageSection' | translate }}</h2>
        <dl class="pricing-detail__facts">
          <div>
            <dt>{{ 'admin.pricing.create.baseRateLabel' | translate }}</dt>
            <dd>{{ formatRate(storage.baseRateByndPerM3Day) }}</dd>
          </div>
          <div>
            <dt>{{ 'admin.pricing.create.minVolumeLabel' | translate }}</dt>
            <dd>{{ storage.minVolumeM3 }}</dd>
          </div>
          <div>
            <dt>{{ 'admin.pricing.create.tiersSection' | translate }}</dt>
            <dd>{{ storage.volumeDiscountTiers.length }}</dd>
          </div>
        </dl>
      </section>

      @if (storage.serviceRates) {
        <section class="pricing-detail__section">
          <h2>{{ 'admin.pricing.create.serviceRatesSection' | translate }}</h2>
          <dl class="pricing-detail__facts">
            <div>
              <dt>{{ 'admin.pricing.create.pickupRateLabel' | translate }}</dt>
              <dd>{{ formatRate(storage.serviceRates.pickupByndPerM3) }}</dd>
            </div>
            <div>
              <dt>{{ 'admin.pricing.create.pickupMinVolumeLabel' | translate }}</dt>
              <dd>{{ storage.serviceRates.pickupMinVolumeM3 }}</dd>
            </div>
            <div>
              <dt>{{ 'admin.pricing.create.returnRateLabel' | translate }}</dt>
              <dd>{{ formatRate(storage.serviceRates.returnByndPerM3) }}</dd>
            </div>
            <div>
              <dt>{{ 'admin.pricing.create.returnMinVolumeLabel' | translate }}</dt>
              <dd>{{ storage.serviceRates.returnMinVolumeM3 }}</dd>
            </div>
            <div>
              <dt>{{ 'admin.pricing.create.packagingRateLabel' | translate }}</dt>
              <dd>{{ formatRate(storage.serviceRates.packagingByndPerM3) }}</dd>
            </div>
            <div>
              <dt>{{ 'admin.pricing.create.packagingMinVolumeLabel' | translate }}</dt>
              <dd>{{ storage.serviceRates.packagingMinVolumeM3 }}</dd>
            </div>
          </dl>
        </section>
      }
    }

    @if (domain() === 'moving' && movingConfig(); as moving) {
      <section class="pricing-detail__section">
        <h2>{{ 'admin.pricing.create.movingSection' | translate }}</h2>
        <dl class="pricing-detail__facts">
          <div>
            <dt>{{ 'admin.pricing.movingRates.packingByndPerM3' | translate }}</dt>
            <dd>{{ formatRate(moving.rates.packingByndPerM3) }}</dd>
          </div>
          <div>
            <dt>
              {{ 'admin.pricing.movingRates.transportByndPerM3' | translate }}
            </dt>
            <dd>{{ formatRate(moving.rates.transportByndPerM3) }}</dd>
          </div>
        </dl>
      </section>
    }

    @if (domain() === 'promocodes' && promocodesConfig(); as promos) {
      <section class="pricing-detail__section">
        <h2>{{ 'admin.pricing.create.promocodesSection' | translate }}</h2>
        <dl class="pricing-detail__facts">
          <div>
            <dt>{{ 'admin.pricing.promocodeCount' | translate }}</dt>
            <dd>{{ promos.promocodes.length }}</dd>
          </div>
        </dl>
      </section>
    }

    <section class="pricing-detail__section">
      <div class="pricing-detail__section-header">
        <h2>{{ 'admin.pricing.detail.configSection' | translate }}</h2>
        <button type="button" class="link-button" (click)="toggleRawJson()">
          {{
            (showRawJson()
              ? 'admin.pricing.detail.hideJson'
              : 'admin.pricing.detail.showJson'
            ) | translate
          }}
        </button>
      </div>
      @if (showRawJson()) {
        <pre class="pricing-detail__json">{{ formattedConfig() }}</pre>
      } @else {
        <p class="pricing-detail__hint">
          {{
            (isScheduled()
              ? 'admin.pricing.detail.scheduledHint'
              : 'admin.pricing.detail.readOnlyHint'
            ) | translate
          }}
        </p>
      }
    </section>
  } @else if (loadError()) {
    <p class="pricing-detail__error" role="alert">
      {{ 'admin.pricing.loadError' | translate }}
    </p>
  } @else {
    <p class="pricing-detail__loading">
      {{ 'admin.pricing.detail.notFound' | translate }}
    </p>
  }
</div>
